import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X, Search, Loader2, Trash2, Eye, Pencil, UserPlus, Users, Check, Globe, MapPin } from "lucide-react";
import toast from "react-hot-toast";
import {
  getDatabaseShares,
  getShareTarget,
  shareDatabase,
  updateDatabaseShare,
  revokeDatabaseShare,
} from "../../../api/database";

const TEAL = "#55c5d0";

const initials = (name = "", email = "") => {
  const source = (name || email || "?").trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

const apiError = (e, fallback) =>
  e?.response?.data?.detail || e?.response?.data?.error || fallback;

const EMPTY = { shared_by_me: [], scoped_shares: [], shared_with_me: [], agents: [] };

/**
 * Invite other agents into your Database, and see whose databases you can open.
 *
 * Christian Gravias, Monday 12817267594: "We need a button where agents can
 * invite other agents to have access to there pipeline database. E.g. agents
 * work in teams and they should be able to share data between the teams."
 *
 * Then (2026-08-18): "it also needs to be a share button for each individual
 * group... we might have 3 agents who are only invited to this one group or we
 * might have public groups that the whole of Collings could be invited to."
 * So the same modal runs in two modes. With no `target` it shares the WHOLE
 * Database, as before. With a `target` — { scope: 'group', view, group_name } or
 * { scope: 'list', list, label } — it shares just that suburb heading or that
 * one tab, and offers "everyone at Collings" instead of a named colleague.
 *
 * The share takes effect immediately — there is no accept step for the invited
 * agent to sit on — and it is one-directional, so "Share both ways" is offered
 * for the common case of two agents working a suburb together. Uploads, lists
 * and columns always stay with the owner; `can_edit` only decides whether a
 * colleague can work the rows (edit cells, log notes, book call-backs).
 */
export default function ShareDatabaseModal({
  onClose,
  onChanged,
  onOpenDatabase,
  currentAgentId,
  target = null,
  onOpenTarget,
}) {
  const scoped = Boolean(target);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(EMPTY);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(new Set());
  const [canEdit, setCanEdit] = useState(false);
  const [bothWays, setBothWays] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = scoped ? await getShareTarget(target) : await getDatabaseShares();
      setData(res.data || EMPTY);
    } catch (e) {
      toast.error(apiError(e, "Couldn't load sharing settings"));
    } finally {
      setLoading(false);
    }
    // `target` is a fresh object each render, so key the callback on its parts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoped, target?.scope, target?.view, target?.group_name, target?.list]);

  useEffect(() => {
    load();
  }, [load]);

  // Everyone already invited is listed in the top section instead, so the
  // picker only offers colleagues who don't have access yet.
  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data.agents || [])
      .filter((a) => !a.shared)
      .filter((a) => !q || `${a.name} ${a.email}`.toLowerCase().includes(q));
  }, [data.agents, query]);

  // Who has access right now. In target mode the API sends the grants on that
  // one group/tab; otherwise it's the whole-database shares.
  const shared = (scoped ? data.shares : data.shared_by_me) || [];
  const scopedShares = data.scoped_shares || [];
  const sharedWithMe = data.shared_with_me || [];
  const everyone = data.everyone || { shared: false };
  const label = scoped ? data.label || target.label || "this group" : "your Database";

  const toggle = (id) => {
    const next = new Set(picked);
    next.has(id) ? next.delete(id) : next.add(id);
    setPicked(next);
  };

  const refresh = (payload) => {
    if (payload) setData(payload);
    onChanged?.(payload);
  };

  const invite = async () => {
    if (!picked.size) return;
    setSaving(true);
    try {
      const res = await shareDatabase({
        agent_ids: [...picked],
        can_edit: canEdit,
        both_ways: bothWays,
        ...(scoped ? target : {}),
      });
      refresh(res.data);
      setPicked(new Set());
      setBothWays(false);
      const n = res.data?.created ?? picked.size;
      toast.success(
        `${scoped ? `"${label}"` : "Database"} shared with ${n} agent${n === 1 ? "" : "s"}`
      );
    } catch (e) {
      toast.error(apiError(e, "Couldn't share that"));
    } finally {
      setSaving(false);
    }
  };

  // "Public group": everyone at Collings, granted once rather than agent by
  // agent, so a new starter walks into the same access.
  const shareWithEveryone = async (nextCanEdit) => {
    setSaving(true);
    try {
      const res = await shareDatabase({
        everyone: true,
        can_edit: nextCanEdit,
        ...target,
      });
      refresh(res.data);
      toast.success(`"${label}" is open to everyone at Collings`);
    } catch (e) {
      toast.error(apiError(e, "Couldn't share with everyone"));
    } finally {
      setSaving(false);
    }
  };

  const setPermission = async (share, nextCanEdit) => {
    try {
      const res = await updateDatabaseShare(share.id, { can_edit: nextCanEdit });
      // The PATCH answers with the whole-database picture; in target mode we
      // want this target's own list back, so reload it instead.
      if (scoped) load();
      else refresh(res.data);
    } catch (e) {
      toast.error(apiError(e, "Couldn't change access"));
    }
  };

  const revoke = async (share, who) => {
    if (!window.confirm(`Remove ${who}'s access to ${scoped ? `"${label}"` : "your Database"}?`))
      return;
    try {
      const res = await revokeDatabaseShare(share.id);
      if (scoped) {
        await load();
        onChanged?.(null);
      } else {
        refresh(res.data);
      }
      toast.success(`${who} can no longer see ${scoped ? `"${label}"` : "your Database"}`);
    } catch (e) {
      toast.error(apiError(e, "Couldn't remove access"));
    }
  };

  const stopSharingWithEveryone = async () => {
    if (!window.confirm(`Stop sharing "${label}" with everyone at Collings?`)) return;
    try {
      await revokeDatabaseShare(everyone.share_id);
      await load();
      onChanged?.(null);
    } catch (e) {
      toast.error(apiError(e, "Couldn't remove access"));
    }
  };

  const leave = async (share, who) => {
    if (!window.confirm(`Remove ${who}'s Database from your switcher?`)) return;
    try {
      // A colleague can share several suburbs separately; dropping them from
      // the switcher means dropping all of it, not just the first grant.
      const ids = share.share_ids?.length ? share.share_ids : [share.id];
      let payload = null;
      for (const id of ids) payload = (await revokeDatabaseShare(id)).data;
      refresh(payload);
      // Viewing the database that was just dropped would 403 on the next load.
      if (currentAgentId && String(currentAgentId) === String(share.owner)) {
        onOpenDatabase?.(null);
      }
    } catch (e) {
      toast.error(apiError(e, "Couldn't remove that database"));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[80] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-[560px] max-w-full max-h-[88vh] flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              {scoped ? <MapPin size={16} style={{ color: TEAL }} /> : <Users size={16} style={{ color: TEAL }} />}
              {scoped ? `Share "${label}"` : "Share your Database"}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {scoped
                ? `Whoever you add here sees only ${
                    target.scope === "list" ? "this list" : "this group's lists"
                  } — the rest of your Database stays private.`
                : "Invite agents you work with. They see your owners and buyers, and can work the rows if you give them edit access."}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 mt-0.5">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-400">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : (
            <>
              {/* Everyone at Collings — only for a group or a single tab. Handing
                  a whole Database to the agency isn't offered: it would quietly
                  undo every private database at once. */}
              {scoped && (
                <section>
                  <h4 className="text-[11px] uppercase tracking-wide text-gray-400 font-bold mb-2">
                    Everyone at Collings
                  </h4>
                  <div
                    className={`flex items-center gap-3 border rounded-lg px-3 py-2 ${
                      everyone.shared ? "border-[#55c5d0] bg-[#eaf7f8]" : "border-gray-200"
                    }`}
                  >
                    <span
                      className="w-8 h-8 shrink-0 rounded-full text-white flex items-center justify-center"
                      style={{ background: everyone.shared ? TEAL : "#cbd5e1" }}
                    >
                      <Globe size={15} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-bold text-gray-900">
                        {everyone.shared ? "Open to the whole office" : "Make this a public group"}
                      </span>
                      <span className="block text-[11px] text-gray-500">
                        {everyone.shared
                          ? `Every agent can ${everyone.can_edit ? "view and edit" : "view"} it, including new starters.`
                          : "Every agent gets access without being invited one by one."}
                      </span>
                    </span>
                    {everyone.shared ? (
                      <>
                        <select
                          value={everyone.can_edit ? "edit" : "view"}
                          onChange={(e) => shareWithEveryone(e.target.value === "edit")}
                          className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold bg-white"
                        >
                          <option value="view">Can view</option>
                          <option value="edit">Can view and edit</option>
                        </select>
                        <button
                          onClick={stopSharingWithEveryone}
                          title="Stop sharing with everyone"
                          className="text-rose-600 p-1.5 rounded-lg hover:bg-rose-50"
                        >
                          <Trash2 size={15} />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => shareWithEveryone(false)}
                        disabled={saving}
                        className="px-3 py-1.5 rounded-lg text-white text-xs font-bold disabled:opacity-50"
                        style={{ background: TEAL }}
                      >
                        Share with everyone
                      </button>
                    )}
                  </div>
                </section>
              )}

              {/* Who already has access */}
              <section className={scoped ? "border-t border-gray-100 pt-4" : ""}>
                <h4 className="text-[11px] uppercase tracking-wide text-gray-400 font-bold mb-2">
                  {scoped
                    ? `Agents with access to "${label}" (${shared.length})`
                    : `Has access to your Database (${shared.length})`}
                </h4>
                {!shared.length ? (
                  <p className="text-sm text-gray-500">
                    {scoped
                      ? "No one yet. Add the agents working this group."
                      : "Nobody yet. Your Database is private to you."}
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {shared.map((s) => {
                      const who = s.agent_detail?.name || s.agent_detail?.email || "Agent";
                      return (
                        <li
                          key={s.id}
                          className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-2"
                        >
                          <span
                            className="w-8 h-8 shrink-0 rounded-full text-white text-[11px] font-bold flex items-center justify-center"
                            style={{ background: TEAL }}
                          >
                            {initials(s.agent_detail?.name, s.agent_detail?.email)}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-bold text-gray-900 truncate">{who}</span>
                            <span className="block text-[11px] text-gray-500 truncate">
                              {s.agent_detail?.email}
                            </span>
                          </span>
                          <select
                            value={s.can_edit ? "edit" : "view"}
                            onChange={(e) => setPermission(s, e.target.value === "edit")}
                            className="border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-semibold"
                          >
                            <option value="view">Can view</option>
                            <option value="edit">Can view and edit</option>
                          </select>
                          <button
                            onClick={() => revoke(s, who)}
                            title="Remove access"
                            className="text-rose-600 p-1.5 rounded-lg hover:bg-rose-50"
                          >
                            <Trash2 size={15} />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>

              {/* Invite */}
              <section className="border-t border-gray-100 pt-4">
                <h4 className="text-[11px] uppercase tracking-wide text-gray-400 font-bold mb-2">
                  {scoped ? "Add agents to this group" : "Invite agents"}
                </h4>
                <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 mb-2">
                  <Search size={15} className="text-gray-400" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search agents by name or email…"
                    className="flex-1 outline-none text-sm"
                  />
                </div>
                <div className="max-h-44 overflow-y-auto border border-gray-100 rounded-lg divide-y divide-gray-100">
                  {!candidates.length && (
                    <p className="text-sm text-gray-500 px-3 py-3">
                      {query ? "No agents match that search." : "Everyone already has access."}
                    </p>
                  )}
                  {candidates.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => toggle(a.id)}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50"
                    >
                      <span
                        className={`w-4 h-4 shrink-0 rounded border flex items-center justify-center ${
                          picked.has(a.id) ? "text-white" : "border-gray-300"
                        }`}
                        style={picked.has(a.id) ? { background: TEAL, borderColor: TEAL } : undefined}
                      >
                        {picked.has(a.id) && <Check size={12} />}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-gray-900 truncate">
                          {a.name}
                        </span>
                        <span className="block text-[11px] text-gray-500 truncate">{a.email}</span>
                      </span>
                      {/* Someone who can already open the whole Database doesn't
                          need this group as well — say so instead of letting an
                          agent think the access is missing. */}
                      <span className="ml-auto text-[10px] uppercase font-bold text-gray-400 shrink-0">
                        {a.full_access ? "Sees everything" : a.role}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="mt-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCanEdit(false)}
                      className={`flex-1 px-3 py-2 rounded-lg border text-xs font-bold flex items-center justify-center gap-1.5 ${
                        canEdit ? "border-gray-200 text-gray-600" : "border-[#55c5d0] bg-[#eaf7f8] text-[#0e7d86]"
                      }`}
                    >
                      <Eye size={13} /> Can view
                    </button>
                    <button
                      onClick={() => setCanEdit(true)}
                      className={`flex-1 px-3 py-2 rounded-lg border text-xs font-bold flex items-center justify-center gap-1.5 ${
                        canEdit ? "border-[#55c5d0] bg-[#eaf7f8] text-[#0e7d86]" : "border-gray-200 text-gray-600"
                      }`}
                    >
                      <Pencil size={13} /> Can view and edit
                    </button>
                  </div>
                  {!scoped && (
                    <label className="flex items-center gap-2 text-xs font-semibold text-gray-600">
                      <input
                        type="checkbox"
                        checked={bothWays}
                        onChange={(e) => setBothWays(e.target.checked)}
                      />
                      Share both ways (they share their Database with me too)
                    </label>
                  )}
                  <p className="text-[11px] text-gray-400">
                    CSV uploads, lists and columns stay yours. You can change or remove
                    access at any time.
                  </p>
                </div>
              </section>

              {/* Groups and tabs shared one at a time, so they can all be seen
                  and revoked from the main Share button too. */}
              {!scoped && scopedShares.length > 0 && (
                <section className="border-t border-gray-100 pt-4">
                  <h4 className="text-[11px] uppercase tracking-wide text-gray-400 font-bold mb-2">
                    Groups and lists you've shared ({scopedShares.length})
                  </h4>
                  <ul className="space-y-2">
                    {scopedShares.map((s) => (
                      <li
                        key={s.id}
                        className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-2"
                      >
                        <span className="w-8 h-8 shrink-0 rounded-full bg-[#eaf7f8] text-[#0e7d86] flex items-center justify-center">
                          {s.everyone ? <Globe size={15} /> : <MapPin size={15} />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-bold text-gray-900 truncate">
                            {s.target_label}
                          </span>
                          <span className="block text-[11px] text-gray-500 truncate">
                            {s.everyone
                              ? "Everyone at Collings"
                              : s.agent_detail?.name || s.agent_detail?.email}{" "}
                            · {s.can_edit ? "can view and edit" : "view only"}
                          </span>
                        </span>
                        {onOpenTarget && (
                          <button
                            onClick={() =>
                              onOpenTarget({
                                scope: s.scope,
                                view: s.view,
                                group_name: s.group_name,
                                list: s.prospect_list,
                                label: s.target_label,
                              })
                            }
                            className="text-xs font-bold text-[#0e7d86] px-2 py-1.5 rounded-lg hover:bg-gray-50"
                          >
                            Manage
                          </button>
                        )}
                        <button
                          onClick={() =>
                            revoke(
                              s,
                              s.everyone ? "everyone" : s.agent_detail?.name || "That agent"
                            )
                          }
                          title="Remove access"
                          className="text-rose-600 p-1.5 rounded-lg hover:bg-rose-50"
                        >
                          <Trash2 size={15} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {/* Databases shared with me */}
              {!scoped && (
                <section className="border-t border-gray-100 pt-4">
                  <h4 className="text-[11px] uppercase tracking-wide text-gray-400 font-bold mb-2">
                    {/* The super admin and a dev/QA account have every database
                        here by role rather than by invitation, so don't call
                        those a share. */}
                    {data.review_access
                      ? `Databases you can open (${sharedWithMe.length})`
                      : `Shared with you (${sharedWithMe.length})`}
                  </h4>
                  {!sharedWithMe.length ? (
                    <p className="text-sm text-gray-500">
                      No other agent has shared their Database with you yet.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {sharedWithMe.map((s) => {
                        const who = s.owner_detail?.name || s.owner_detail?.email || "Agent";
                        return (
                          <li
                            key={s.id || `share-${s.owner}`}
                            className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-2"
                          >
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-bold text-gray-900 truncate">
                                {who}
                              </span>
                              <span className="block text-[11px] text-gray-500">
                                {s.record_count} record{s.record_count === 1 ? "" : "s"} ·{" "}
                                {s.via_review
                                  ? data.review_reason === "principal"
                                    ? "view only (super admin)"
                                    : "view only (tech access)"
                                  : s.can_edit
                                  ? "can view and edit"
                                  : "view only"}
                              </span>
                              {/* Part of a Database rather than all of it: name
                                  the suburbs so it's clear what will open. */}
                              {!s.full && s.targets?.length > 0 && (
                                <span className="block text-[11px] text-[#0e7d86] font-semibold truncate">
                                  {s.targets.join(", ")}
                                </span>
                              )}
                            </span>
                            <button
                              onClick={() => {
                                onOpenDatabase?.(s.owner, who, s.can_edit);
                                onClose();
                              }}
                              className="px-3 py-1.5 rounded-lg text-white text-xs font-bold"
                              style={{ background: TEAL }}
                            >
                              Open
                            </button>
                            {/* Every row here is a real share now, so every row
                                can be walked away from. It used to be able to
                                be an admin's oversight entry, which there was
                                nothing to revoke on. */}
                            {s.id && (
                              <button
                                onClick={() => leave(s, who)}
                                title="Remove this database from my switcher"
                                className="text-gray-400 p-1.5 rounded-lg hover:bg-gray-50"
                              >
                                <X size={15} />
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200">
          <button onClick={onClose} className="text-sm font-semibold text-gray-600">
            Close
          </button>
          <button
            onClick={invite}
            disabled={saving || !picked.size}
            className="px-4 py-2 rounded-lg text-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
            style={{ background: TEAL }}
          >
            <UserPlus size={15} />
            {saving
              ? "Sharing…"
              : `Share with ${picked.size || ""} agent${picked.size === 1 ? "" : "s"}`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
}
